import { useGaiaWebsocket } from "@expert/gaia";
import { useCallback } from "react";

import { useAnalytics } from "@/analytics";
import { useCallDetailsStore } from "@/call-details";
import { type CustomerDetailsPayload, useVerifiedCustomerStore } from "@/customer-info";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useDetectedCuesStore } from "@/sales-tools/detected-cues/stores/DetectedCuesStore";
import { useRapportBuildingStore } from "@/sales-tools/rapport-building/useRapportBuildingStore";
import { usePartner, useTypedMostRecentTask } from "@/sdk";
import { getHashedValue, removeCountryCode, validatePhoneNumber } from "@/shared-utils";

import { useVerificationFlowStore } from "../../customer-verification-store";
import { buildConfirmedCustomerData } from "../utils/buildConfirmedCustomerData";
import { buildGaiaSessionProperties, sanitizeGaiaSessionContextProperties } from "../utils/buildGaiaSessionProperties";
import { buildPartnerToolUrl } from "../utils/buildPartnerToolUrl";
import { buildSupportedProfileAnalyticsPayload } from "../utils/buildSupportedProfileAnalyticsPayload";
import { determineCustomerVerificationStatus } from "../utils/determineCustomerVerificationStatus";
import { env } from "../utils/environment";

export interface ConfirmCustomerVerificationParams {
    sessionId: string;
    customerName: string;
    mdn: string;
    mdnFromTask: string;
    verificationType: "phoneNumber" | "billingAccountNumberAndPin";
    customerData: CustomerDetailsPayload | undefined;
    showAdditionalDetailsEmailInput: boolean;
    showAdditionalDetailsOrderIdInput: boolean;
    additionalDetailsEmail: string;
    additionalDetailsOrderId: string;
    additionalDetailsMdn: string;
    billingAccountNumber: string;
    currentSubscriptions: string[] | undefined;
    flushDebouncedUpdates: () => void;
}

export interface UseConfirmCustomerVerificationReturn {
    /**
     * Handles the confirm button click. Updates stores, sends analytics,
     * updates Gaia session context, and opens partner tool if applicable.
     */
    confirmCustomerVerification: (params: ConfirmCustomerVerificationParams) => Promise<void>;
}

export function useConfirmCustomerVerification(): UseConfirmCustomerVerificationReturn {
    const gaiaWS = useGaiaWebsocket();
    const { dispatcher } = useAnalytics();
    const partner = usePartner();
    const { partnerToolUrl } = usePartnerConfig();
    const { task: mostRecentTask, isVoice, voiceTask } = useTypedMostRecentTask();

    const confirmCustomerVerification = useCallback(
        async (params: ConfirmCustomerVerificationParams) => {
            const {
                sessionId,
                customerName,
                mdn,
                mdnFromTask,
                verificationType,
                customerData,
                showAdditionalDetailsEmailInput,
                showAdditionalDetailsOrderIdInput,
                additionalDetailsEmail,
                additionalDetailsOrderId,
                additionalDetailsMdn,
                billingAccountNumber,
                currentSubscriptions,
                flushDebouncedUpdates,
            } = params;

            if (!customerName || (verificationType === "phoneNumber" && !mdn) || !gaiaWS) {
                return;
            }

            flushDebouncedUpdates();

            const confirmedData = buildConfirmedCustomerData({
                verificationType,
                mdn,
                additionalDetailsMdn,
                customerName,
                customerData,
                showAdditionalDetailsEmailInput,
                additionalDetailsEmail,
                showAdditionalDetailsOrderIdInput,
                additionalDetailsOrderId,
                billingAccountNumber,
                currentSubscriptions,
            });

            const { confirmedMdn, confirmedEmail, confirmedAccountNumber, customerToSave } = confirmedData;
            const formattedMdn = removeCountryCode(confirmedMdn);

            if (customerData?.previousDevices) {
                useDetectedCuesStore.getState().processPreviouslyDetectedCues(sessionId, customerData.previousDevices);
                useRapportBuildingStore.getState().updateLocation({
                    city: customerData.primaryAccountHolder?.address?.City,
                    state: customerData.primaryAccountHolder?.address?.StateCode,
                    timezone: customerData.primaryAccountHolder?.address?.TimeZone?.Name,
                });
            }

            const { setCustomer, setAsurionAgreements, setPartnerAgreements } = useVerifiedCustomerStore.getState();
            setCustomer(sessionId, customerToSave, customerData?.deviceActivation);
            setAsurionAgreements(sessionId, customerData?.asurionAgreements);
            setPartnerAgreements(sessionId, customerData?.partnerAgreements);

            void dispatcher
                .withExtra({ partnerAgreements: customerData?.partnerAgreements })
                .dispatchBusinessEvent("CustomerPartnerAgreements");

            const hashedMdn = await getHashedValue(formattedMdn);
            const gaiaProperties = buildGaiaSessionProperties(
                {
                    mdn: formattedMdn,
                    countryCode: validatePhoneNumber(confirmedMdn).countryCode,
                    clientChannelId: customerData?.clientChannelId,
                    previousDevices: customerData?.previousDevices,
                },
                hashedMdn,
            );

            void gaiaWS.sendJsonMessage({
                action: "update-session-context",
                correlationId: mostRecentTask?.sessionId,
                sessionId: isVoice && voiceTask ? voiceTask.agentCallId : mostRecentTask?.sessionId,
                sessionGroupId: mostRecentTask?.sessionId,
                partner,
                context: {
                    properties: sanitizeGaiaSessionContextProperties(gaiaProperties),
                },
            });

            const callDetails = useCallDetailsStore.getState().callDetails;
            const profilePayload = buildSupportedProfileAnalyticsPayload({
                customerData,
                callDetails,
                currentSubscriptions,
            });

            void dispatcher
                .withMeta({ Client: partner, SkipMixpanel: true, SkipOpenSearch: true })
                .withExtra({
                    ...profilePayload,
                })
                .withIdentities({
                    MDN: formattedMdn,
                    Email: confirmedEmail,
                    AccountNumber: confirmedAccountNumber,
                })
                .dispatchBusinessEvent("SupportedProfileDisplayed");

            void dispatcher
                .withMeta({ Client: partner, SkipMixpanel: true })
                .withExtra({ currentSubscriptions: currentSubscriptions ?? [] })
                .dispatchBusinessEvent("CurrentSubscriptionsCaptured");

            void dispatcher
                .withMeta({ SkipMixpanel: true, SkipEDP: true, isTest: true, SkipOpenSearch: true })
                .withExtra({
                    confirmedMdn,
                    mdnFromTask,
                })
                .dispatchBusinessEvent("MdnConfirmedByExpert");

            const authorizedUsers = Array.from(customerData?.authorizedUsers ?? []);
            if (customerData?.primaryAccountHolder) {
                authorizedUsers.push(customerData.primaryAccountHolder);
            }

            const partnerToolResult = buildPartnerToolUrl({
                partner,
                partnerToolUrl,
                isDevMode: env.isDevMode,
                customerName,
                formattedMdn,
                authorizedUsers,
                sessionId,
                determineVerificationStatus: determineCustomerVerificationStatus,
            });

            if (partnerToolResult.shouldOpen && partnerToolResult.url) {
                window.open(partnerToolResult.url, "_blank", "noopener,noreferrer");
            }

            const { pushStep } = useVerificationFlowStore.getState();
            pushStep(sessionId, "VerificationComplete");
        },
        [gaiaWS, dispatcher, partner, partnerToolUrl, mostRecentTask, isVoice, voiceTask],
    );

    return {
        confirmCustomerVerification,
    };
}
